import {
  Alert,
  Button,
  Card,
  Center,
  Loader,
  Select,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconBolt, IconCircleCheck } from '@tabler/icons-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { api, type SourcePair } from '../lib/api.js';

/**
 * Public page. A dealership is redirected here from the CRM's own portal after
 * activating the app.
 *
 * We do not yet know which query parameter CDK appends, so accept the likely
 * spellings rather than guessing one. Narrow this once a real redirect is seen.
 */
const ID_PARAMS = ['subscriptionId', 'subscription_id', 'subscriptionid', 'id'];

interface Info {
  subscriptionId: string;
  orgName: string;
  alreadyConfigured: boolean;
  missingConfig: string[];
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <Center mih="100vh" p="md">
      <Stack w={480} gap="lg">
        <Center>
          <ThemeIcon size={40} radius="md" variant="light">
            <IconBolt size={24} />
          </ThemeIcon>
        </Center>
        {children}
      </Stack>
    </Center>
  );
}

/**
 * Ask for a portal account.
 *
 * Deliberately only a request: this page is public and the subscription id in
 * its URL is not a secret, so submitting grants nothing. Someone at our end
 * approves it, and the invitation then goes to the address given here.
 */
function AccessRequestCard({ subscriptionId }: { subscriptionId: string }) {
  const form = useForm({
    initialValues: { email: '', name: '' },
    validate: { email: (v) => (/^\S+@\S+\.\S+$/.test(v) ? null : 'Enter a valid email address') },
  });
  const send = useMutation({
    mutationFn: (v: typeof form.values) =>
      api.post('/provision/request-access', { subscriptionId, email: v.email, name: v.name }),
  });

  return (
    <Card>
      <Title order={4}>Get your dealership portal</Title>
      {send.isSuccess ? (
        <Alert color="green" variant="light" mt="md" icon={<IconCircleCheck size={18} />}>
          Request received. Once we have approved it, an invitation will be emailed to you.
        </Alert>
      ) : (
        <>
          <Text size="sm" c="dimmed" mt="xs" mb="md">
            See your enquiries and your AI agent&rsquo;s calls in one place. Tell us who should
            have access and we will send an invitation once it is approved.
          </Text>
          <form onSubmit={form.onSubmit((v) => send.mutate(v))}>
            <Stack>
              <TextInput label="Your name" placeholder="Optional" {...form.getInputProps('name')} />
              <TextInput
                label="Your email address"
                placeholder="you@yourdealership.com"
                required
                {...form.getInputProps('email')}
              />
              {send.isError && <Alert color="red">{(send.error as Error).message}</Alert>}
              <Button type="submit" variant="light" loading={send.isPending} fullWidth>
                Request portal access
              </Button>
            </Stack>
          </form>
        </>
      )}
    </Card>
  );
}

export function ProvisionPage() {
  const [params] = useSearchParams();
  const subscriptionId = ID_PARAMS.map((p) => params.get(p)).find(Boolean) ?? '';

  const info = useQuery({
    queryKey: ['provision', subscriptionId],
    queryFn: () => api.get<Info>(`/provision/${subscriptionId}`),
    enabled: Boolean(subscriptionId),
    retry: false,
  });

  const pairsQ = useQuery({
    queryKey: ['provision-pairs', subscriptionId],
    queryFn: () => api.get<{ pairs: SourcePair[] }>(`/provision/${subscriptionId}/source-pairs`),
    enabled: Boolean(subscriptionId) && info.isSuccess,
    retry: false,
  });

  const form = useForm({
    initialValues: { fromEmail: '', pair: '' },
    validate: {
      fromEmail: (v) => (/^\S+@\S+\.\S+$/.test(v) ? null : 'Enter a valid email address'),
      pair: (v) => (v ? null : 'Please choose one'),
    },
  });

  const submit = useMutation({
    mutationFn: (v: typeof form.values) => {
      const [source, upType] = v.pair.split(' ');
      return api.post('/provision', { subscriptionId, fromEmail: v.fromEmail, source, upType });
    },
  });

  if (!subscriptionId) {
    return (
      <Shell>
        <Card>
          <Title order={4}>Setup link incomplete</Title>
          <Text size="sm" c="dimmed" mt="xs">
            Please open this page from the <b>Configure App</b> link in your CRM&rsquo;s dealer
            portal.
          </Text>
        </Card>
      </Shell>
    );
  }

  if (info.isLoading) {
    return (
      <Shell>
        <Center>
          <Loader />
        </Center>
      </Shell>
    );
  }

  if (info.isError) {
    return (
      <Shell>
        <Card>
          <Title order={4}>We couldn&rsquo;t find that subscription</Title>
          <Text size="sm" c="dimmed" mt="xs">
            It can take a few minutes after activating the app. If this persists, contact support and
            quote <Text span ff="monospace">{subscriptionId}</Text>.
          </Text>
        </Card>
      </Shell>
    );
  }

  if (submit.isSuccess || info.data?.alreadyConfigured) {
    return (
      <Shell>
        <Card>
          <Stack align="center" gap="sm">
            <ThemeIcon size={44} radius="xl" color="green" variant="light">
              <IconCircleCheck size={26} />
            </ThemeIcon>
            <Title order={4}>You&rsquo;re all set</Title>
            <Text size="sm" c="dimmed" ta="center">
              {info.data?.orgName} is configured. Your AI agent can now read leads and book
              appointments in your CRM. Contact support if these details need changing.
            </Text>
          </Stack>
        </Card>
        <AccessRequestCard subscriptionId={subscriptionId} />
      </Shell>
    );
  }

  const pairs = pairsQ.data?.pairs ?? [];
  const pairOptions = pairs.map((p) => ({
    value: `${p.source} ${p.upType}`,
    label: `${p.source} — ${p.upType}`,
  }));

  return (
    <Shell>
      <Card>
        <Title order={4}>Finish setting up {info.data?.orgName}</Title>
        <Text size="sm" c="dimmed" mt="xs" mb="md">
          Two details we can&rsquo;t read automatically from your CRM. You only do this once.
        </Text>

        <form onSubmit={form.onSubmit((v) => submit.mutate(v))}>
          <Stack>
            <TextInput
              label="Sending email address"
              description="Emails from your AI agent come from this address. It must already be set up as a sending address in your CRM."
              placeholder="sales@yourdealership.com"
              required
              {...form.getInputProps('fromEmail')}
            />

            <Select
              label="Lead source and up type"
              description="How new enquiries should be filed. These are combinations already in use at your dealership."
              placeholder={pairOptions.length > 0 ? 'Choose one' : 'Loading your options…'}
              data={pairOptions}
              disabled={pairOptions.length === 0}
              searchable
              required
              {...form.getInputProps('pair')}
            />

            {submit.isError && <Alert color="red">{(submit.error as Error).message}</Alert>}

            <Button type="submit" loading={submit.isPending} fullWidth mt="xs">
              Finish setup
            </Button>
          </Stack>
        </form>
        {pairOptions.length === 0 && !pairsQ.isLoading && (
          <Text size="xs" c="dimmed" mt="sm">
            The list stays empty until your first enquiries have been collected. If you cannot
            choose one yet, request portal access below and we will finish this with you.
          </Text>
        )}
      </Card>
      <AccessRequestCard subscriptionId={subscriptionId} />
    </Shell>
  );
}
